<script setup>
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';

import {inputComposable} from 'Components/inputs/composables/inputComposable';


/**
 * A wrapper around vuetify's v-text-field component.
 */
const props = defineProps({
    /**
     * Called when suffix icon is clicked.
     */
    onSuffixIconClick: Function,
    /**
     * Icon that is displayed on the right side of the text field.
     * Ideally icons of the icon16x16 font are used here.
     */
    suffixIconClass: String,
    /**
     * String that is displayed as the hint for the text field when the it's readonly.
     */
    readonlyHint: {
      type: String
    },
    /**
     * Specifies whether the text field is disabled (true) or not (false).
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-disabled
     */
    // TODO(VHA) Rename to `disabled`
    isDisabled: Boolean,
    /**
     * Specifies whether the text field is readonly (true) or not (false).
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-readonly
     */
    readonly: Boolean,
    /**
     * Specifies whether the solo style is applied (true) or not (false).
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-solo
     */
    solo: Boolean,
    /**
     * Specifies whether the outlined style is applied (true) or not (false).
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-outlined
     */
    outlined: Boolean,
    /**
     * Specifies whether the hint is always visible (true) or not (false).
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-persistent-hint
     */
    persistentHint: Boolean,
    /**
     * The type of the text field.
     * @values text, number, password
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-type
     */
    type: {
      type: String,
      default: 'text',
    },
    /**
     * Text displayed on the right side of the text field.
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-suffix
     */
    textSuffix: String,
    /**
     * Placeholder for the text field.
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-placeholder
     */
    placeholder: String,
    /**
     * String that is displayed as the label for the text field.
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-label
     */
    label: String,
    /**
     * The validation rules for the text field.
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-rules
     */
    rules: Array,
    /**
     * Specifies whether text field is marked as required (true) or not (false).
     * Required text fields have a "*" added to the label.
     */
    required: Boolean,
    /**
     * Specifies whether validation is delayed until blur event (true) or not (false).
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-validate-on-blur
     */
    validateOnBlur: Boolean,
    /**
     * String that is displayed as the hint for the text field.
     * See: https://vuetifyjs.com/en/api/v-text-field/#props-hint
     */
    hint: String,
  });
const modelValue = defineModel('modelValue');
const {t} = useI18n();
const emit = defineEmits(['input', 'update:modelValue']);
const {
  variant,
  density
} = inputComposable();

const onSuffixIconClickHandler = computed(() => {
  if (enableAppendIconClick.value) {
    return props.onSuffixIconClick;
  }
  return null;
});
const readonlyHintComputed = computed(() => {
  return props.readonlyHint ?? t('common.inputs.readonlyHint');
});
const variantComputed = computed(() => {
  if (props.outlined) return 'outlined';
  if (props.solo) return 'solo';

  return variant.value;
});
const enableAppendIconClick = computed(() => {
  return !props.isDisabled && !!props.onSuffixIconClick && content.value?.length > 0;
});
const labelComputed = computed(() => {
  return props.required ? `${props.label} *` : props.label;
});

const content = computed({
  get() {
    return modelValue.value;
  },

  set(value) {
    /**
     * Triggers when the value has changed.
     */
    emit('input', value);
    emit('update:modelValue', value);
  },
});
</script>

<template>
  <v-text-field
      class="input-text"
      ref="vTextField"
      v-model="content"
      @click:append-inner="onSuffixIconClickHandler"
      :append-inner-icon="suffixIconClass"
      :label="labelComputed"
      :type="type"
      :placeholder=placeholder
      :rules="rules"
      :disabled="isDisabled"
      :suffix="textSuffix"
      :required="required"
      :validate-on-blur="validateOnBlur"
      :readonly="readonly"
      :hint="readonly ? readonlyHintComputed : hint"
      :persistent-hint="persistentHint"
      :variant="variantComputed"
      :density="density"
      bg-color="white"
      base-color="rgba(89, 89, 89, 0.5)"
      color="#979797"
  >

    <template v-for="(slot, name) in $slots" :key="name" v-slot:[name]="slotProps">
      <slot :name="name" v-bind="slotProps"/>
    </template>
  </v-text-field>
</template>

<style lang="scss" scoped>
@use "Styles/helper/variables";

:deep(.v-field__field) {
  input {
    font-size: 1.25rem !important;
    color: rgb(84, 84, 84) !important;
  }
}
</style>
